import { ComponentRef, Injector, Signal, TemplateRef, Type } from '@angular/core';
import { OverlayRef } from '@angular/cdk/overlay';

import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Close types with data to be returned as result
 */
export interface KrosModalCloseEvent<T = any> {
  type: 'submit' | 'cancel' | 'backdrop' | 'redirect';
  data: T | null;
}

/**
 * Modal can take TemplateRef / Component / string to be displayed within
 */
export type ModalContent = TemplateRef<any> | Type<any> | string;

/**
 * Additional settings
 */
export interface KrosModalSettings {
  /** Determines whether the modal should be closed after clicking on the backdrop */
  closeOnBackdropClick: boolean;

  /** Allows to hide default mobile modal header with arrow-back button */
  showMobileArrowBack: boolean;

  /** Determines whether modal should be expanded to fullscreen or not */
  fullscreen: boolean;

  /** Determines whether modal should be fullscreen on mobile or not */
  fullscreenOnMobile: boolean;

  /** Determines whether modal should auto capture focus */
  allowFocusAutocapture: boolean;

  /** Include modal in browser's history as "#modal" and close it on step back */
  addModalToBrowsersHistory: boolean;

  /** Determines whether the modal should be closed after pressing Escape */
  closeOnEscClick: boolean;

  /** Allows to hide X button to close modal/messagebox */
  showXButton: boolean;

  /** If user's screen is smaller as modal height + 215px and not use openCentered() set this properties */
  setSmallScreen?: boolean;

  /** Adds a gradient to the bottom of the modal window. TODO: If we want - Add a new property to set the gradient position */
  addGradient?: boolean;

  /** Observable that determines if the accept button is ready to be clicked */
  acceptButtonReady$?: Observable<boolean>;

  /**
   * Ignores the behavior of allowing only one modal at a time.
   */
  ignoreMultipleModalsBehavior?: boolean;

  /** Determines whether the modal needs to be confirmed by entering specific text */
  confirmByText: boolean;

  /**
   * Element that triggered the modal opening (used for focus return after modal is closed)
   */
  openingElement?: HTMLElement;

  /**
   * Injector of the component opening the modal. When set, it is used as the parent injector
   * for the modal so the modal (and its content) can resolve providers scoped to the opener
   * (e.g. route/submodule-level providers). Falls back to the modal service injector when unset.
   */
  injector?: Injector;

  /**
   * Inputs of a component content, set by `openCenteredShell()` only. A signal, so the content gets new values while
   * the modal is open.
   */
  contentInputs?: Signal<Record<string, unknown>>;

  /**
   * A sheet on a phone, for `openCenteredShell()` only: there the shell leaves the viewport to its content, which draws
   * the sheet on the bottom edge (a `modal-layout` drawer reads this setting from the `KrosModalRef`), and a tap beside
   * the sheet closes the modal as the backdrop would. Nothing changes on a desktop.
   */
  sheetOnPhone?: boolean;
}

export function getDefaultKrosModalSettings(): KrosModalSettings {
  return {
    closeOnBackdropClick: true,
    closeOnEscClick: true,
    showMobileArrowBack: true,
    fullscreen: false,
    fullscreenOnMobile: true,
    allowFocusAutocapture: true,
    addModalToBrowsersHistory: false,
    showXButton: true,
    addGradient: false,
    confirmByText: false,
  };
}

export class KrosModalRef<T = any> {
  beforeClose$;
  afterClosed$;
  componentRef: ComponentRef<any> | null = null;
  isFullscreen: boolean;

  get fullscreenIcon(): string {
    return this.isFullscreen ? 'close_fullscreen' : 'open_in_full';
  }

  private beforeClose = new Subject<KrosModalCloseEvent<T>>();
  private afterClosed = new Subject<KrosModalCloseEvent<T>>();

  constructor(
    public overlay: OverlayRef,
    public content: ModalContent,
    public settings: KrosModalSettings,
    public data?: T | null
  ) {
    this.beforeClose$ = this.beforeClose.asObservable().pipe(take(1));
    this.afterClosed$ = this.afterClosed.asObservable().pipe(take(1));
    if (this.settings.closeOnBackdropClick) {
      // Click on backdrop acts as close, if is this behavior enabled
      overlay
        .backdropClick()
        .pipe(take(1))
        .subscribe((_) => this.close('backdrop', null));
    }
    this.isFullscreen = settings.fullscreen;
  }

  submit(data?: T): void {
    this.close('submit', data);
  }

  cancel(result: KrosModalCloseEvent['type'] = 'cancel'): void {
    this.close(result, null);
  }

  redirect(data?: T): void {
    this.close('redirect', data);
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  private close(type: KrosModalCloseEvent['type'], data: T | null = null): void {
    const closeValue = { type, data };

    this.beforeClose.next(closeValue);
    this.overlay.dispose();

    // timeout is needed to keep modal chains working
    // otherwise it will open modal in the same moment as it is closing the old one
    setTimeout(() => this.afterClosed.next(closeValue), 50);
  }
}
